import { alphaSort, alphaSorter } from 'vet-bones/utils';

import { ProjectPropertyType } from 'src/constants/project';
import { ModelSnapshot } from 'src/containers/ProjectDiagram/types/model';
import { isModelConceptRelationship } from 'src/containers/ProjectDiagram/utils/data';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import {
  OWL_OBJECT_PROPERTY_IRI,
  RDFS_COMMENT_IRI,
  RDFS_LABEL_IRI,
} from 'src/templates/queries/iris';
import { escapeDoubleQuotes } from 'src/utils/escapeDoubleQuotes';
import { getAbbreviationOrFullIri } from 'src/utils/getAbbreviationOrFullIri';
import { getInheritedRelationshipsByClass } from 'src/utils/getInheritedRelationshipsByClass';
import { getPropertyIrisForModelConcept } from 'src/utils/stringify/getPropertyIrisForModelConcept';

const modelConceptRelationshipsSort = alphaSort(
  (item: [string, any]) => item[0]
);

export const stringifyModelConceptRelationships = (
  model: ModelSnapshot,
  projectPropertyType: ProjectPropertyType,
  namespaces: Record<string, string>,
  filter?: (
    relationship: [
      string,
      {
        domains: Set<string>;
        ranges: Set<string>;
      }
    ]
  ) => boolean,
  classIris?: string[]
): string => {
  const abbreviate = (iri: string) => getAbbreviationOrFullIri(iri, namespaces);

  const relationshipsByIri = model.linkDataArray
    .filter(isModelConceptRelationship)
    .reduce<
      Record<
        string,
        {
          domains: Set<string>;
          ranges: Set<string>;
        }
      >
    >((acc, data) => {
      if (!acc[data.value]) {
        acc[data.value] = {
          domains: new Set(),
          ranges: new Set(),
        };
      }

      if (data.from) {
        const from = getModelConceptClassValue(data.from);
        acc[data.value].domains.add(from);
      }

      if (data.to) {
        const to = getModelConceptClassValue(data.to);
        acc[data.value].ranges.add(to);
      }

      return acc;
    }, {});

  if (classIris) {
    const inheritedRelationshipsByClass = getInheritedRelationshipsByClass(
      model,
      classIris
    );

    Object.keys(inheritedRelationshipsByClass).forEach((classIri) => {
      Object.keys(inheritedRelationshipsByClass[classIri]).forEach(
        (relationshipIri) => {
          const obj =
            inheritedRelationshipsByClass[classIri]?.[relationshipIri];
          relationshipsByIri[relationshipIri] = {
            ...relationshipsByIri[relationshipIri],
            domains: new Set([
              ...relationshipsByIri[relationshipIri].domains,
              ...obj.domains,
            ]),
            ranges: new Set([
              ...relationshipsByIri[relationshipIri].ranges,
              ...obj.ranges,
            ]),
          };
        }
      );
    });
  }

  let relationships = Object.entries(relationshipsByIri);
  if (filter) {
    relationships = relationships.filter(filter);
  }

  return relationships
    .sort(modelConceptRelationshipsSort)
    .map(([value, { domains, ranges }]) => {
      const abbreviated = abbreviate(value);

      const lines = [`${abbreviated} a ${abbreviate(OWL_OBJECT_PROPERTY_IRI)}`];

      const label = model.modelData.labelsByIri[value];
      if (label) {
        lines.push(
          `${abbreviate(RDFS_LABEL_IRI)} "${escapeDoubleQuotes(label)}"`
        );
      }

      const description = model.modelData.descriptionsByIri[value];
      if (description) {
        lines.push(
          `${abbreviate(RDFS_COMMENT_IRI)} "${escapeDoubleQuotes(description)}"`
        );
      }

      const { domainIri, rangeIri } = getPropertyIrisForModelConcept(
        projectPropertyType,
        model,
        value
      );

      if (domains.size) {
        const objects = Array.from(domains)
          .map((object) => abbreviate(object))
          .sort(alphaSorter)
          .join(', ');
        lines.push(`${abbreviate(domainIri)} ${objects}`);
      }

      if (ranges.size) {
        const objects = Array.from(ranges)
          .map((object) => abbreviate(object))
          .sort(alphaSorter)
          .join(', ');
        lines.push(`${abbreviate(rangeIri)} ${objects}`);
      }

      return `${lines.join(' ;\n  ')} .`;
    })
    .join('\n');
};
